import {HttpHeaders, HttpClient,
	HttpParams}                     from "@angular/common/http";
import {Injectable}                 from '@angular/core';

import {AngularQuery}               from "../query/angularquery";
import {PageSettings}               from "../news/page-settings";
import {User}                       from "./user";

import {concat}                     from "rxjs/observable/concat";
import {map}                        from "rxjs/operators";
import {Observable}                 from "rxjs/Observable";


@Injectable()
export class UserService {

	/**
	 * Initialize the HttpClient object.
	 * @param {HttpClient} http
	 */
	constructor(protected http: HttpClient) {}


	/**
	 * Delete the specified user from the database.
	 *
	 * @param {number} id  The ID of the User record to delete.
	 * @return {Observable<Boolean>} Returns true if the user was deleted.
	 */
	deleteUser(id:number): Observable<Boolean> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER, id.toString());
		query.useParams(false);
		query.useCacheBuster(false);
		let url:string = query.getUrl();

		return this.http
			.delete<Boolean>(url)
			.pipe(
				map(() => true)
			);

	}


	/**
	 * Get information on all users for the
	 * user administration screen.
	 *
	 * @return {Observable<User[]>}
	 */
	getUsers():Observable<User[]> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER);
		return this.http
			.get<User[]>(query.getUrl());

	}


    /**
	 * Communicate with the ReST API to authenticate
	 * the user and store their information in the
	 * localStorage object.
	 *
 	 * @param {string} username
	 * @param {string} password
	 * @return {Observable<User>}
	 */
	login(username: string, password: string):Observable<User> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER_LOGIN);
		query.useParams(false);
		let headers = new HttpHeaders().append('Content-Type', 'application/x-www-form-urlencoded');
	    let body: any = new HttpParams()
		    .set('uname', username)
		    .set('password', password);
		return this.http
			.post<User>(query.getUrl(), body.toString(),
				{ headers: headers })
			.pipe(
				map(
					(response:User) => {
						let user:User = new User(response);
						// Login successful if there's a session_id in the response.
						if (user && user.session_id) {

							// Store user details in local storage to keep user logged in between page refreshes.
							sessionStorage.setItem('currentUser', JSON.stringify(user));

							// Load the variables that persist between sessions.
							if (user.persistence) {

								let settings:PageSettings = new PageSettings(JSON.parse(atob(user.persistence)));
								let s:string = JSON.stringify(settings);
								sessionStorage.setItem('pageSettings', s);

							}

						}
						return user;
					}
				)
			);

	}


	/**
	 * Log out of the system.
	 */
	logout():void {

		// Kill the session on the server.
		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER_LOGOUT);
		query.useParams(false);

		let getter:any = null;

		// Save the persistent settings to the database, if they are available.
		let page:string = sessionStorage.getItem('pageSettings');
		let userString:string = sessionStorage.getItem('currentUser');

		if (null !== page && null !== userString) {

			let user:User = new User(JSON.parse(userString));
			let uquery:AngularQuery = new AngularQuery();
			uquery.setEndpoint(AngularQuery.ENDPOINT_USER, user.id.toString());
			uquery.useParams(false);
			uquery.useCacheBuster(false);
			let headers = new HttpHeaders().append('Content-Type', 'application/json');
			let body:string = "{\"persistence\": \"" + btoa(page) + "\"}";

			getter = concat(this.http.put<User>(uquery.getUrl(), body, { headers: headers } ),this.http.get<any>(query.getUrl()));

		} else {

			getter = concat(this.http.get<any>(query.getUrl()));

		}

		// Perform both operations; do not report errors or completion.
		const subscribe = getter.subscribe();

	}


	/**
	 * Retrieve report data about one or more users.
	 *
	 * @param {string} users        A comma-separated list of one or more user IDs.
	 * @param {string} start_date   The start date for the retrieval.
	 * @param {string} end_date     The end date for the retrieval.
	 * @param {string} table        Either 'user_actions' or 'user_sessions'.
	 * @return {Observable<Array<Object>>} The JSON data.
	 */
	report(users:string, start_date:string, end_date:string, table:string): Observable<Array<Object>> {

		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER_REPORT);
		query.addParameter('users', encodeURIComponent(users));
		query.addParameter('start_date', start_date);
		query.addParameter('end_date', end_date);
		query.addParameter('table', table);
		return this.http.get<Array<Object>>(query.getUrl());

	}


	/**
	 * Create or save a user in the database.
	 *
	 * @param {User} user   The User to save.
	 * @return {Observable<User>} The User data returned by the database.
	 */
	save(user:User): Observable<User> {

		let headers = new HttpHeaders().append('Content-Type', 'application/json');
		let query:AngularQuery = new AngularQuery();

		if (! user.id || -1 === user.id) {

			query.setEndpoint(AngularQuery.ENDPOINT_USER, 'new');
			user.id = 0;

		} else {

			query.setEndpoint(AngularQuery.ENDPOINT_USER, user.id.toString());

		}

		user.password = (user.password) ? user.password.trim() : '';

		return this.http
			.post<User>(query.getUrl(), JSON.stringify(user), { headers: headers } );

	}


	/**
	 * Update the tracking of the user's session.
	 *
	 * @return {Observable<any>}  The API's 201 response.
	 */
	track(): Observable<any> {

		let query: AngularQuery = new AngularQuery();
		query.useParams(false);
		query.setEndpoint(AngularQuery.ENDPOINT_USER_TRACK);

		return this.http
			.get<any>(query.getUrl());

	}


	/**
	 * Update a user in the database.  This updates ONLY those fields
	 * that are included in the User object.
	 *
	 * @param {User} user   The User object with fields to update.
	 * @return {Observable<User>} The User data returned by the database.
	 */
	update(user:User): Observable<User> {

		let headers = new HttpHeaders().append('Content-Type', 'application/json');
		let query:AngularQuery = new AngularQuery();
		query.setEndpoint(AngularQuery.ENDPOINT_USER, user.id.toString());

		return this.http
			.put<User>(query.getUrl(), JSON.stringify(user), { headers: headers } );

	}


}



// WEBPACK FOOTER //
// ./src/app/services/user/user.service.ts